import { ColumnDef } from '@tanstack/react-table';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { StylishNewCheckbox } from 'components/DesignSystems/New/StylishNewCheckbox';
import StylishNewInput from 'components/DesignSystems/New/StylishNewInput';
import PratusTable from 'components/DesignSystems/PratusTable/PratusTable';
import { useUpsertIncidentStatusSummary } from 'components/IAP/hooks/incidentStatusSummaryHooks';
import AssignedResourcesEquipmentTypesDrawer from 'components/IAP/planningView/widgets/IncidentStatusSummary/IncidentStatusSummaryComplexComponents/AssignedResourcesEquipmentTypesDrawer';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { useCallback, useMemo, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useToggle } from 'react-use';
import {
  IncidentStatusSummaryDto,
  PerAgencyResources,
} from 'types/iapBaseTypes';

const APPENDED_ROW_ID = 'APPENDED_ROW_ID';

const mapAssignedResourcesFormFieldsToPayload = (
  assignedResources: PerAgencyResources[] = []
): IncidentStatusSummaryDto['assignedResources'] => {
  const payloadResources: IncidentStatusSummaryDto['assignedResources'] = [];

  assignedResources.forEach((agencyResource) => {
    const { agency, personnelCount, equipment } = agencyResource;
    payloadResources.push({
      agency,
      type: 'PERSONNEL',
      amount: personnelCount,
    });

    equipment.forEach((eq) => {
      payloadResources.push({
        agency,
        type: eq.type,
        amount: eq.quantity,
      });
    });
  });

  return payloadResources;
};

const headerCell = (label: string) => (
  <span className="text-nowrap">{label}</span>
);

const AssignedResourcesTable = ({
  statusSummaryId,
  originalAssignedResources,
}: {
  statusSummaryId: string;
  originalAssignedResources: PerAgencyResources[];
}) => {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [rowBeingEditedId, setRowBeingEditedId] = useState<string | null>(null);
  const [editEquipmentTypesOpen, toggleEditEquipmentTypesDrawer] =
    useToggle(false);
  const upsertIncidentStatusSummary =
    useUpsertIncidentStatusSummary(statusSummaryId);

  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      assignedResources: originalAssignedResources,
    },
  });

  const { control, register, getValues } = form;

  const { insert, remove, replace, update, fields } = useFieldArray({
    control,
    name: 'assignedResources',
  });

  const rows = useMemo(() => {
    let rows = fields.filter((field) =>
      field.agency.toLowerCase().includes(search.toLowerCase())
    );
    const finalField = fields[fields.length - 1];

    if (
      finalField &&
      !rows.some((field) => field.id === finalField.id) &&
      rowBeingEditedId === APPENDED_ROW_ID
    ) {
      rows = rows.concat(finalField);
    }

    const totalsMap: Map<string, number> = new Map();
    rows.forEach((row) => {
      row.equipment.forEach((eq) => {
        const currentTotal = totalsMap.get(eq.type) || 0;
        totalsMap.set(eq.type, currentTotal + parseInt(eq.quantity, 10));
      });
    });

    const personnelTotal = rows.reduce((acc, row) => {
      return acc + parseInt(row.personnelCount, 10);
    }, 0);

    rows.push({
      id: 'total-row',
      agency: 'Total',
      personnelCount: personnelTotal.toString(),
      equipment:
        totalsMap.size > 0
          ? Array.from(totalsMap.entries()).map(([type, quantity]) => ({
              type,
              quantity: quantity.toString(),
            }))
          : [],
    });

    return rows;
  }, [fields, search, rowBeingEditedId]);

  const allSelected = useMemo(() => {
    if (!rows.length) return false;

    // Exclude the total row from selection count
    return rows.length - 1 === selectedIds.length;
  }, [rows, selectedIds]);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds(
      allSelected
        ? []
        : rows.filter((row) => row.id !== 'total-row').map((field) => field.id)
    );
  }, [allSelected, rows]);

  const toggleRow = useCallback(
    (id: string) => {
      const set = new Set(selectedIds);
      if (set.has(id)) {
        set.delete(id);
      } else {
        set.add(id);
      }
      setSelectedIds([...set]);
    },
    [selectedIds]
  );

  const deleteSelected = useCallback(() => {
    const toDeleteIndexes = fields.reduce<number[]>((acc, field, index) => {
      if (selectedIds.includes(field.id)) {
        acc.push(index);
      }
      return acc;
    }, []);

    upsertIncidentStatusSummary({
      assignedResources: mapAssignedResourcesFormFieldsToPayload(
        getValues('assignedResources').filter(
          (_: PerAgencyResources, index: number) =>
            !toDeleteIndexes.includes(index)
        )
      ),
    });

    remove(toDeleteIndexes);
    setSelectedIds([]);
  }, [
    remove,
    fields,
    selectedIds,
    getValues,
    upsertIncidentStatusSummary,
    setSelectedIds,
  ]);

  const isRowEditable = useCallback(
    (rowId: string, rowIndex: number): boolean => {
      //N.B. -2 here because of the total row
      return (
        rowBeingEditedId === rowId ||
        (rowBeingEditedId === APPENDED_ROW_ID && rowIndex === rows.length - 2)
      );
    },
    [rowBeingEditedId, rows.length]
  );

  const deleteRow = useCallback(
    (rowToDelete: PerAgencyResources & { id: string }) => {
      const index = fields.findIndex((field) => field.id === rowToDelete.id);
      if (index !== -1) {
        remove(index);
      }
      setSelectedIds((prev) => prev.filter((x) => x !== rowToDelete.id));
      upsertIncidentStatusSummary({
        assignedResources: mapAssignedResourcesFormFieldsToPayload(
          getValues('assignedResources').filter(
            (_: PerAgencyResources, idx: number) => idx !== index
          ) as PerAgencyResources[]
        ),
      });
    },
    [remove, fields, getValues, upsertIncidentStatusSummary, setSelectedIds]
  );

  const equipmentList = useMemo(() => {
    const equipmentSet = new Set<string>();
    fields.forEach((field) => {
      field.equipment.forEach((eq) => {
        // Only add valid equipment types (non-empty strings)
        if (eq.type && eq.type.trim()) {
          equipmentSet.add(eq.type);
        }
      });
    });

    return Array.from(equipmentSet);
  }, [fields]);

  const handleSaveEquipmentTypes = useCallback(
    (updatedEquipmentTypes: Set<string>) => {
      const newEquipmentList = Array.from(updatedEquipmentTypes);

      const updatedFields = fields.map((field) => {
        const updatedEquipment = newEquipmentList.map((type) => {
          const existingEntry = field.equipment.find((eq) => eq.type === type);
          return existingEntry ? existingEntry : { type, quantity: '0' }; // Default quantity to '0' for new types
        });

        return {
          ...field,
          equipment: updatedEquipment,
        };
      });

      replace(updatedFields);
      upsertIncidentStatusSummary({
        assignedResources: mapAssignedResourcesFormFieldsToPayload(
          getValues('assignedResources')
        ),
      });
      toggleEditEquipmentTypesDrawer();
    },

    [
      fields,
      replace,
      upsertIncidentStatusSummary,
      getValues,
      toggleEditEquipmentTypesDrawer,
    ]
  );

  const appendBlankAgency = useCallback(() => {
    insert(fields.length, {
      agency: '',
      personnelCount: '0',
      equipment: equipmentList.map((et) => ({ type: et, quantity: '0' })),
    });
    setRowBeingEditedId(APPENDED_ROW_ID);
  }, [insert, equipmentList, fields.length]);

  const columns = useMemo<ColumnDef<PerAgencyResources & { id: string }>[]>(
    () => [
      {
        id: 'select',
        header: () => (
          <StylishNewCheckbox
            checked={allSelected}
            onChange={toggleSelectAll}
            disabled={rows.length === 0 || rowBeingEditedId !== null}
          />
        ),
        cell: ({ row }) => {
          if (row.original.id !== 'total-row') {
            return (
              <StylishNewCheckbox
                checked={selectedIds.includes(row.original.id)}
                onChange={() => {
                  toggleRow(row.original.id);
                }}
                disabled={rowBeingEditedId !== null}
              />
            );
          }
        },
        size: 10,
        enableSorting: false,
        meta: { headerAlign: 'left' },
      },
      {
        accessorKey: 'agency',
        header: () => headerCell('Agency'),
        cell: ({ row }) => {
          if (isRowEditable(row.original.id, row.index)) {
            const fieldIndex = fields.findIndex(
              (field) => field.id === row.original.id
            );

            return (
              <StylishNewInput
                className="form-control"
                placeholder="Enter agency"
                defaultValue={row.original.agency}
                {...register(`assignedResources.${fieldIndex}.agency`)}
              />
            );
          } else {
            return <span>{row.original.agency}</span>;
          }
        },
        enableSorting: true,
        enableResizing: false,
        meta: { headerAlign: 'left' },
      },
      {
        accessorKey: 'people',
        header: () => headerCell('People'),
        cell: ({ row }) => {
          if (isRowEditable(row.original.id, row.index)) {
            const fieldIndex = fields.findIndex(
              (field) => field.id === row.original.id
            );

            return (
              <input
                className="form-control"
                placeholder="Enter number of people"
                defaultValue={row.original.personnelCount}
                {...register(`assignedResources.${fieldIndex}.personnelCount`)}
              />
            );
          } else {
            return <span>{row.original.personnelCount}</span>;
          }
        },
        enableSorting: true,
        enableResizing: false,
        meta: { headerAlign: 'left' },
      },
      ...equipmentList.map<ColumnDef<PerAgencyResources & { id: string }>>(
        (equipmentType) => ({
          accessorKey: equipmentType,
          header: () => headerCell(equipmentType),
          cell: ({ row }) => {
            const equipmentEntryIndex = row.original.equipment.findIndex(
              (eq) => eq.type === equipmentType
            );

            const quantity =
              equipmentEntryIndex !== -1
                ? row.original.equipment[equipmentEntryIndex].quantity
                : 0;

            if (isRowEditable(row.original.id, row.index)) {
              const fieldIndex = fields.findIndex(
                (field) => field.id === row.original.id
              );

              return (
                <input
                  className="form-control"
                  type="number"
                  placeholder={`Qty of ${equipmentType}`}
                  defaultValue={quantity}
                  {...register(
                    `assignedResources.${fieldIndex}.equipment.${equipmentEntryIndex}.quantity`
                  )}
                />
              );
            } else {
              return <span>{quantity}</span>;
            }
          },
          enableSorting: true,
          meta: { headerAlign: 'left' },
        })
      ),
      {
        id: 'actions',
        header: () => <span />,
        cell: ({ row }) => {
          if (row.original.id !== 'total-row') {
            if (isRowEditable(row.original.id, row.index)) {
              const fieldIndex = fields.findIndex(
                (field) => field.id === row.original.id
              );

              return (
                <div className="d-flex gap-1 justify-content-end">
                  <StylishNewButton
                    className="button--secondary border-0"
                    onClick={(e) => {
                      e.stopPropagation();

                      const currentValues = getValues('assignedResources');
                      upsertIncidentStatusSummary({
                        assignedResources:
                          mapAssignedResourcesFormFieldsToPayload(
                            currentValues
                          ),
                      });
                      update(fieldIndex, {
                        agency: currentValues[fieldIndex].agency,
                        personnelCount:
                          currentValues[fieldIndex].personnelCount,
                        equipment: currentValues[fieldIndex].equipment,
                      });
                      setRowBeingEditedId(null);
                    }}
                  >
                    <SharedIcon iconName="check" classes="me-1" />
                  </StylishNewButton>
                  <StylishNewButton
                    className="button--secondary border-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      update(fieldIndex, {
                        agency: fields[fieldIndex].agency,
                        personnelCount: fields[fieldIndex].personnelCount,
                        equipment: fields[fieldIndex].equipment,
                      });
                      setRowBeingEditedId(null);
                    }}
                  >
                    <SharedIcon iconName="cancel" classes="me-1" />
                  </StylishNewButton>
                </div>
              );
            } else {
              return (
                <div className="d-flex gap-1 justify-content-end">
                  <StylishNewButton
                    className="button--secondary border-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedIds([]);
                      setRowBeingEditedId(row.original.id);
                    }}
                    disabled={rowBeingEditedId !== null}
                  >
                    <SharedIcon iconName="edit" classes="me-1" />
                  </StylishNewButton>
                  <StylishNewButton
                    className="button--secondary border-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteRow(row.original);
                    }}
                    disabled={rowBeingEditedId !== null}
                  >
                    <SharedIcon iconName="delete" classes="me-1" />
                  </StylishNewButton>
                </div>
              );
            }
          }
        },
        enableSorting: false,
        size: 160,
        meta: { headerAlign: 'left' },
      },
    ],
    [
      allSelected,
      selectedIds,
      toggleRow,
      toggleSelectAll,
      register,
      deleteRow,
      rowBeingEditedId,
      isRowEditable,
      setRowBeingEditedId,
      fields,
      getValues,
      update,
      equipmentList,
      upsertIncidentStatusSummary,
      rows.length,
    ]
  );

  return (
    <>
      <FormProvider {...form}>
        <div className="d-flex flex-column justify-content-between flex-grow-1">
          <div className="d-flex justify-content-between align-items-end mb-2 gap-2 flex-wrap">
            <div
              className="flex-grow-1 justify-content-between align-items-center gap-2 d-flex"
              style={{ minWidth: 240 }}
            >
              <label style={{ whiteSpace: 'nowrap' }}>Assigned Resources</label>
              <StylishNewInput
                className="form-control"
                placeholder="Search assigned resources..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div style={{ paddingLeft: '4rem' }}>
              <StylishNewButton
                className="button button--secondary button--sml"
                onClick={(e) => {
                  e.stopPropagation();
                  appendBlankAgency();
                }}
                disabled={rowBeingEditedId !== null}
              >
                <SharedIcon iconName="add" classes="me-2" />
                Add Agency
              </StylishNewButton>
            </div>
            <div style={{ paddingLeft: '1rem' }}>
              <StylishNewButton
                className="button button--primary button--sml"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleEditEquipmentTypesDrawer();
                }}
              >
                <SharedIcon iconName="add" classes="me-2" />
                Edit Equipment Types
              </StylishNewButton>
            </div>
          </div>

          {selectedIds.length > 0 && (
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div>{selectedIds.length} selected</div>
              <StylishNewButton
                className="button--error button--sml"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteSelected();
                }}
              >
                <SharedIcon iconName="delete" classes="me-1" /> Delete Selected
              </StylishNewButton>
            </div>
          )}
          <PratusTable
            className="layout-auto pratus-table"
            columns={columns}
            data={rows}
            getRowId={(row) => row.id}
            noDataIndication={
              <div className="text-center p-2">
                {search
                  ? 'No results!'
                  : 'There are no agencies yet. Click + Add Agency to add an agency.'}
              </div>
            }
            footer={
              <span className="text-nowrap me-2">{`${rows.length} agencies`}</span>
            }
          />
          {editEquipmentTypesOpen ? (
            <AssignedResourcesEquipmentTypesDrawer
              equipmentTypes={equipmentList}
              toggle={toggleEditEquipmentTypesDrawer}
              onSave={handleSaveEquipmentTypes}
            />
          ) : null}
        </div>
      </FormProvider>
    </>
  );
};

export default AssignedResourcesTable;
